import React, {
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import { XCircle } from "lucide-react";
import ModalScreenProps from "../../model/ModalScreenProps";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const isVisible = (element: HTMLElement, boundary: HTMLElement): boolean => {
  let node: HTMLElement | null = element;
  while (node && node !== boundary) {
    if (node.hidden || node.getAttribute("aria-hidden") === "true") {
      return false;
    }
    const style = window.getComputedStyle(node);
    if (style.display === "none" || style.visibility === "hidden") {
      return false;
    }
    node = node.parentElement;
  }
  return true;
};

const ModalScreen = React.forwardRef(function ModalScreen(
  {
    className,
    children,
    footer,
    childrenControlOverflow = false,
    title,
    state,
    onClose,
    onShow,
    dismissOnEscape = false,
  }: ModalScreenProps,
  ref: React.Ref<{ close: () => void }>,
) {
  const [modalState, setModalState] = useState(state);
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const titleId = useId();

  const handleCloseModal = useCallback(() => {
    setModalState("closed");
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (modalState === "closed") return;

    previouslyFocusedRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    panelRef.current?.focus();

    return () => {
      const target = previouslyFocusedRef.current;
      if (target?.isConnected) {
        target.focus();
      }
    };
  }, [modalState]);

  useEffect(() => {
    if (modalState === "closed") return;

    const onKeyDown = (event: KeyboardEvent) => {
      // Never intercept keystrokes that belong to an IME composition.
      if (event.isComposing) return;

      const panel = panelRef.current;
      if (!panel) return;

      // Focus may sit on <body> after clicking non-interactive content, so the
      // handler lives on document rather than the panel.
      const target = event.target as HTMLElement | null;
      const nested = target?.closest?.('[role="dialog"], [aria-modal="true"]');
      const nestedDialog =
        nested && nested !== panel && panel.contains(nested)
          ? (nested as HTMLElement)
          : null;

      if (event.key === "Escape") {
        if (nestedDialog || !dismissOnEscape) return;
        event.stopPropagation();
        handleCloseModal();
        return;
      }

      if (event.key !== "Tab") return;

      const trapRoot = nestedDialog ?? panel;
      const focusable = Array.from(
        trapRoot.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((element) => isVisible(element, trapRoot));
      if (focusable.length === 0) {
        event.preventDefault();
        trapRoot.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (!active || active === trapRoot || !trapRoot.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [modalState, handleCloseModal, dismissOnEscape]);

  const getWindowClassName = useCallback(() => {
    switch (modalState) {
      case "confirm":
        return "bg-yellow-100 text-yellow-800 max-w-md";
      case "alert":
        return "bg-red-100 text-red-800 max-w-md";
      case "info":
        return "bg-blue-100 text-blue-800 max-w-md";
      case "view":
        return "bg-white w-full md:w-5/6 lg:w-3/6 lg:max-h-[80vh]";
      case "big-view":
      case "edit":
        return "bg-white w-full lg:w-4/6 lg:max-h-[80vh]";
      case "fullscreen":
        return "bg-white w-full h-full max-h-screen max-w-screen";
      default:
        return "bg-gray-100 text-gray-800";
    }
  }, [modalState]);

  const getContentClassName = useCallback(() => {
    switch (modalState) {
      case "confirm":
      case "alert":
      case "info":
        return "p-4 min-h-[64px] overflow-y-auto";
      case "big-view":
      case "view":
      case "edit":
        return "p-6 min-h-[64vh] overflow-y-auto w-full";
      case "fullscreen":
        return "p-0 h-full overflow-y-auto w-full";
      default:
        return "p-4";
    }
  }, [modalState]);

  useImperativeHandle(
    ref,
    () => ({
      close: handleCloseModal,
    }),
    [handleCloseModal],
  );

  return (
    <AnimatePresence>
      {modalState !== "closed" && (
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          transition={{ duration: 0.3 }}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              panelRef.current?.focus();
            }
          }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto"
        >
          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={title ? titleId : undefined}
            tabIndex={-1}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
            className={`rounded-xl shadow-5xl overflow-y-auto outline-none ${className ?? ""} ${getWindowClassName()}`}
          >
            <div className="p-3 sticky top-0 bg-gray-900/90 backdrop-blur-md text-white border-b modal-header">
              <div className="flex justify-between items-center">
                <span
                  id={title ? titleId : undefined}
                  className="text-xl font-bold"
                >
                  {title}
                </span>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  aria-label="Fechar"
                  className="is-large"
                >
                  <XCircle className="w-6 h-6" aria-hidden="true" />
                </button>
              </div>
            </div>
            {childrenControlOverflow ? (
              children
            ) : (
              <div className={`max-h-full ${getContentClassName()}`}>
                {children}
              </div>
            )}
            {footer && (
              <div className="sticky bottom-0 bg-gray-900/90 backdrop-blur-md text-white border-t modal-footer">
                {footer}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
});

export default ModalScreen;
